/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * How match_value is interpreted: exact (finding text), regex (RE2 pattern over finding text), rule_id, source, or entity_type (presidio entity, matched as rule_id 'pii.<entity>').
 */
export const RiskExclusionMatchType = {
  Exact: "exact",
  Regex: "regex",
  RuleId: "rule_id",
  Source: "source",
  EntityType: "entity_type",
} as const;
/**
 * How match_value is interpreted: exact (finding text), regex (RE2 pattern over finding text), rule_id, source, or entity_type (presidio entity, matched as rule_id 'pii.<entity>').
 */
export type RiskExclusionMatchType = ClosedEnum<typeof RiskExclusionMatchType>;

export type RiskExclusion = {
  /**
   * When the exclusion was created.
   */
  createdAt: Date;
  /**
   * Whether the exclusion is active.
   */
  enabled: boolean;
  /**
   * The exclusion ID.
   */
  id: string;
  /**
   * How match_value is interpreted: exact (finding text), regex (RE2 pattern over finding text), rule_id, source, or entity_type (presidio entity, matched as rule_id 'pii.<entity>').
   */
  matchType: RiskExclusionMatchType;
  /**
   * The value matched against findings, interpreted per match_type.
   */
  matchValue: string;
  /**
   * The project ID.
   */
  projectId: string;
  /**
   * The policy this exclusion is bound to. Null/omitted means global: the exclusion applies to every policy in the project.
   */
  riskPolicyId?: string | undefined;
  /**
   * Optional narrowing: an exact/regex/source exclusion only applies to findings with this rule_id. Empty means any.
   */
  ruleIdFilter: string;
  /**
   * Optional narrowing: an exact/regex/rule_id exclusion only applies to findings from this source. Empty means any.
   */
  sourceFilter: string;
  /**
   * When the exclusion was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const RiskExclusionMatchType$inboundSchema: z.ZodMiniEnum<
  typeof RiskExclusionMatchType
> = z.enum(RiskExclusionMatchType);

/** @internal */
export const RiskExclusion$inboundSchema: z.ZodMiniType<
  RiskExclusion,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    enabled: z.boolean(),
    id: z.string(),
    match_type: RiskExclusionMatchType$inboundSchema,
    match_value: z.string(),
    project_id: z.string(),
    risk_policy_id: z.optional(z.string()),
    rule_id_filter: z.string(),
    source_filter: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "match_type": "matchType",
      "match_value": "matchValue",
      "project_id": "projectId",
      "risk_policy_id": "riskPolicyId",
      "rule_id_filter": "ruleIdFilter",
      "source_filter": "sourceFilter",
      "updated_at": "updatedAt",
    });
  }),
);

export function riskExclusionFromJSON(
  jsonString: string,
): SafeParseResult<RiskExclusion, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskExclusion$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskExclusion' from JSON`,
  );
}
